import React from 'react';
import {scaleLinear, scaleTime} from 'd3-scale';
import {Chart} from '../Chart';
import SimpleLineSeries from '../series/SimpleLineSeries';
import {Property} from 'csstype';
import {extent} from 'd3-array';
import {DataPoint} from '../../types';
import {bridge} from '../series/bridge';
import SimpleVerticalAxis from '../axes/SimpleVerticalAxis';
import SimpleBottomAxis from '../axes/SimpleBottomAxis';
import styled from 'styled-components';
import {theme} from '../style';
import {SvgSimpleProgressRing} from './SimpleProgressRing';

interface LineBarComboProps {
  series: {
    color: Property.Color;
    data: DataPoint[];
    legendLabel: string;
  }[];
  // while this is technically an array, we only really expect two of them.
  // if you want more progress rings because we're adding more line series,
  // you need to adapt the positioning
  progress: {
    highlight: Property.Color;
    background: Property.Color;
    progress: number;
    label: string;
  }[];
  periods: [{from: Date; to: Date}, {from: Date; to: Date}];
  formatValue?: (n: number) => string;
  background: boolean;
}

/**
 * Shows period over period activity - render sparklines on the left combined with
 * bars on the right. Used for coaching effectiveness.
 */
export class LineProgressCombo extends React.Component<LineBarComboProps> {
  render() {
    const {
      series,
      progress,
      periods: [startPeriod, endPeriod],
      background,
    } = this.props;

    const formatValue = this.props.formatValue
      ? this.props.formatValue
      : (n: number) => n.toString();

    const svgDimensions = {width: 400, height: 180};

    // note the higher top margin! we'll use this space for the legend
    const margins = {top: 70, left: 30, right: 15, bottom: 25};

    const xScale = scaleTime<number, number>()
      .domain([startPeriod.from, endPeriod.to])
      .range([margins.left, svgDimensions.width - margins.right]);

    const values = series.map(s => s.data.map(d => d.value)).flat() as number[];

    const yScale = scaleLinear()
      // @ts-ignore TODO
      .domain(extent([0, Math.max(...values) * 1.2]))
      .range([svgDimensions.height - margins.bottom, margins.top]);

    const innerChartWidth = svgDimensions.width - margins.left - margins.right;
    const innerChartHeight = svgDimensions.height - margins.top - margins.bottom;

    // center the progress rings at 1/4 and 3/4 of the width of the space horizontally,
    // and vertically near the center of the cart, but subtract a fudge amount to
    // fill more of that empty space
    const progressCircleSize = 80;
    const progressAreaStart = innerChartWidth / 2 + margins.left;
    const progressCircleXStarts = [
      progressAreaStart + innerChartWidth / 8 - progressCircleSize / 2,
      progressAreaStart + (innerChartWidth / 8) * 3 - progressCircleSize / 2,
    ];
    const progressCircleYStart = margins.top + innerChartHeight / 2 - progressCircleSize / 2 - 15;

    const vsTopPadding = 10;

    const legendStartY = vsTopPadding + 3 + 22;
    const legendLineHeight = 12;

    return (
      <Chart bordered={false} margins={margins} svgDimensions={svgDimensions}>
        {background && (
          <ChartBackground
            height={innerChartHeight}
            width={innerChartWidth}
            x={margins.left}
            y={margins.top}
          />
        )}
        <Axes>
          <SimpleVerticalAxis
            amtOfTicks={5}
            margins={margins}
            scale={yScale}
            // @ts-ignore
            tickFormat={formatValue}
          />
          <SimpleBottomAxis
            scale={xScale}
            secondPeriodStart={endPeriod.from}
            marginBottom={margins.bottom}
            chartHeight={svgDimensions.height}
          />
        </Axes>
        <LineXLabel
          x={margins.left + innerChartWidth / 4}
          y={svgDimensions.height - margins.bottom / 2 + 4}>
          Activity (Hrs)
        </LineXLabel>
        <DividerLine
          x1={xScale(endPeriod.from)! + 0.5}
          y1={svgDimensions.height - margins.bottom}
          x2={xScale(endPeriod.from)! + 0.5}
          y2={margins.top - 35}
        />
        <g>
          <VSCircle r={10} cx={margins.left + innerChartWidth / 2 + 5} cy={vsTopPadding} />
          <VSText x={margins.left + innerChartWidth / 2 + 5} y={vsTopPadding + 3}>
            VS
          </VSText>
        </g>
        <ChartLegendHeader x={margins.left / 2} y={vsTopPadding + 3}>
          COACHING LAST PERIOD
        </ChartLegendHeader>
        <g>
          {series.map((s, i) => {
            const sum = formatValue(s.data.reduce((a, b) => a + b.value, 0));

            return (
              <g key={i}>
                <line
                  x1={margins.left / 2}
                  y1={legendStartY + legendLineHeight * i - 3}
                  x2={margins.left / 2 + 10}
                  y2={legendStartY + legendLineHeight * i - 3}
                  stroke={s.color}
                  strokeLinecap={'round'}
                  strokeWidth={3}
                />
                <ChartLegendItem x={margins.left / 2 + 15} y={legendStartY + legendLineHeight * i}>
                  {sum} {s.legendLabel}
                </ChartLegendItem>
              </g>
            );
          })}
        </g>
        <ChartLegendHeader x={margins.left + innerChartWidth / 2 + 20} y={vsTopPadding + 3}>
          TEAM IMPROVEMENT THIS PERIOD
        </ChartLegendHeader>
        {series.map((s, i) => (
          <SimpleLineSeries
            key={i}
            data={bridge(startPeriod.from, startPeriod.to, s.data, false)}
            color={s.color}
            xScale={xScale}
            yScale={yScale}
          />
        ))}
        <ProgressRingLabel
          x={progressCircleXStarts[0] + progressCircleSize / 2}
          y={progressCircleYStart}>
          {progress[0].label}
        </ProgressRingLabel>
        <SvgSimpleProgressRing
          backgroundColor={progress[0].background}
          highlightColor={progress[0].highlight}
          progress={progress[0].progress}
          includePlusMinus
          coordinate={{x: progressCircleXStarts[0], y: progressCircleYStart}}
          size={progressCircleSize}
        />
        <ProgressRingLabel
          x={progressCircleXStarts[1] + progressCircleSize / 2}
          y={progressCircleYStart}>
          {progress[1].label}
        </ProgressRingLabel>
        <SvgSimpleProgressRing
          backgroundColor={progress[1].background}
          highlightColor={progress[1].highlight}
          progress={progress[1].progress}
          includePlusMinus
          coordinate={{x: progressCircleXStarts[1], y: progressCircleYStart}}
          size={progressCircleSize}
        />
      </Chart>
    );
  }
}

const DividerLine = styled.line`
  stroke: ${theme.colors.basicallyBlack};
  stroke-width: 1;
`;

const VSCircle = styled.circle`
  fill: ${theme.colors.disabled};
`;

const VSText = styled.text`
  font-weight: 900;
  fill: white;
  text-anchor: middle;
  font-family: ${theme.fonts.primaryFontFamily};
  font-size: 8px;
`;

const ChartLegendHeader = styled.text`
  font-weight: 900;
  text-transform: uppercase;
  fill: ${theme.colors.basicallyBlack};
  font-family: ${theme.fonts.primaryFontFamily};
  font-size: 9px;
`;

const ChartLegendItem = styled.text`
  fill: ${theme.colors.basicallyBlack};
  font-family: ${theme.fonts.primaryFontFamily};
  font-size: 8px;
`;

const LineXLabel = styled.text`
  fill: ${theme.colors.basicallyBlack};
  font-family: ${theme.fonts.primaryFontFamily};
  font-size: 8px;
  font-weight: bold;
  text-transform: uppercase;
  text-anchor: middle;
`;

const ProgressRingLabel = styled.text`
  fill: ${theme.colors.basicallyBlack};
  font-family: ${theme.fonts.primaryFontFamily};
  font-size: 10px;
  text-anchor: middle;
  text-transform: uppercase;
  font-weight: 700;
`;

const Axes = styled.g`
  color: ${theme.colors.basicallyBlack};

  text {
    font-family: ${theme.fonts.primaryFontFamily};
    font-size: 8px;
  }
`;

const ChartBackground = styled.rect`
  fill: ${theme.colors.chartBackground};
`;
